import type { Placement } from '@popperjs/core';
import type { ButtonPriority } from '@timelyapp/react';
import { Button, useDebouncedEffect } from '@timelyapp/react';
import classNames from 'classnames';
import type { DropdownBuilderProps, DropdownOption, RowNode } from 'components/DropdownBuilder';
import DropdownBuilder from 'components/DropdownBuilder';
import { omit } from 'lodash-es';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import FontAwesome from 'react-fontawesome';
import { useNavigate } from 'react-router-dom';
import styles from './Search.module.scss';

interface Props<T extends DropdownOption<number | string>> {
  value: string | number | null; // defaultProp'd to null,
  options: Array<T>;
  onChange: (a: number | string | null, item: T | null) => void;
  onChangeSearchString?: (value: string) => void;
  onSearch?: (filterString: string) => Promise<unknown>;
  optionComponent: React.ComponentType<{
    option: T;
    /** When set, will turn options into links on ${optionAsLinkFromBasePath}${option.value} */
    optionAsLinkFromBasePath?: string;
  }>;
  popoverListHeader?: React.ReactNode;
  matchOption?: (option: T, filterString: string) => boolean;
  popoverFixed?: boolean;
  popoverPosition?: Placement;
  name?: string;
  className?: string;
  shrinkOnBlur?: boolean;
  regularInput?: boolean;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  postProcessOptions?: (
    options: Array<RowNode<number | string, T>>,
    filterString: string,
    addOption: (option: T) => void
  ) => void;
  inputIcon?: any;
  inputValidationError?: string;
  optionHeight: number; // defaultProp'd,
  buttonPriority: ButtonPriority; // defaultProp'd,
  shrinkLabel: React.ReactNode; // defaultProp'd,
  placeholder: string; // defaultProp'd,
  variant: 'default' | 'inHeader' | 'inHourEditor'; // defaultProp'd
  isLoadingOptions?: boolean;
  hideFromFullstory?: boolean;
  onClear?: () => void;
  searchOnMount?: boolean;
  /** When set, will turn options into links on ${optionAsLinkFromBasePath}${option.value} */
  optionAsLinkFromBasePath?: string;
  wide?: boolean;
}

const Search = <T extends DropdownOption<number | string>>(props: Props<T>) => {
  const {
    buttonPriority,
    className,
    hideFromFullstory,
    inputIcon,
    inputValidationError,
    regularInput,
    matchOption,
    name,
    onFocus,
    onBlur,
    onChange,
    onChangeSearchString,
    onSearch,
    optionComponent,
    popoverListHeader,
    optionAsLinkFromBasePath,
    optionHeight,
    options,
    placeholder,
    popoverFixed,
    searchOnMount = true,
    shrinkLabel,
    shrinkOnBlur,
    value,
    variant,
    wide,
    popoverPosition,
    ...rest
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const handleChange = useCallback(
    (v: number | string | null, item: T | null) => {
      if (v && inputRef.current) {
        inputRef.current.blur();
      }
      if (optionAsLinkFromBasePath && item) {
        navigate(`${optionAsLinkFromBasePath}${item.value}`);
      }
      onChange(v, item);
    },
    [optionAsLinkFromBasePath, onChange, navigate]
  );

  // filterString is for user search input only, never derived from value (which is a numeric ID)
  const [filterString, setFilterString] = useState('');

  useDebouncedEffect(() => onSearch?.(filterString), [filterString, onSearch], {
    delay: 300,
    runOnMount: searchOnMount,
  });

  const showExpandSearchButton = shrinkOnBlur && !isFocused;
  const containerClass = classNames(
    styles.container,
    styles[`variant-${variant}`],
    {
      [styles.shrink]: showExpandSearchButton,
    },
    className
  );

  // We're _visually_ hiding the search if shrinkOnBlur, as we need it
  // visible in the DOM to programatically focus on it, ref handleFocus.
  const searchStyle = useMemo(
    () => (showExpandSearchButton ? { display: 'none' } : undefined),
    [showExpandSearchButton]
  );

  const extra: Partial<DropdownBuilderProps<number | string, T>> = {};
  if (optionComponent) {
    const C = optionComponent;

    extra.renderOption = option => {
      return <C option={option} optionAsLinkFromBasePath={optionAsLinkFromBasePath} />;
    };
  }

  return (
    <DropdownBuilder
      popoverFixed={popoverFixed}
      value={value}
      options={options}
      onChange={handleChange}
      placeholder={placeholder}
      inputIcon={inputIcon || <FontAwesome name="search" />}
      inputValidationError={inputValidationError}
      optionHeight={optionHeight}
      inputCaret={false}
      matchOption={matchOption}
      filterString={filterString}
      onSetFilterString={setFilterString}
      regularInput={regularInput}
      {...rest}
      {...extra}
    >
      {context => (
        <div
          data-testid="search_client_or_project"
          className={containerClass}
          {...omit(rest, 'postProcessOptions', 'isLoadingOptions')}
        >
          {showExpandSearchButton && (
            <Button priority={buttonPriority} onClick={() => setIsFocused(true)}>
              {shrinkLabel}
            </Button>
          )}

          {(isFocused || !showExpandSearchButton) &&
            context.renderInput({
              ref: inputRef,
              style: searchStyle,
              onFocus: e => {
                onFocus?.(e);
                setIsFocused(true);
              },
              onBlur: e => {
                onBlur?.(e);
                setIsFocused(false);
              },
              name,
              autoFocus: shrinkOnBlur,
              className: classNames(styles.input, {
                [styles.wide]: wide,
                'fs-mask': hideFromFullstory,
              }),
              onChange: e => {
                onChangeSearchString?.((e.target as HTMLInputElement).value);
              },
            })}

          {context.isOpen && (
            <div>
              {context.renderListPopover({
                renderListProps: { width: wide ? 500 : undefined },
                popoverPosition,
                popoverListHeader,
              })}
            </div>
          )}
        </div>
      )}
    </DropdownBuilder>
  );
};

Search.defaultProps = {
  value: null,
  variant: 'default',
  placeholder: 'Search',
  buttonPriority: 'medium',
  name: '',
  className: '',
  shrinkOnBlur: false,
  shrinkLabel: <FontAwesome name="search" />,
  inputIcon: <FontAwesome name="search" />,
  optionheight: 24,
};

export default Search;
